import React, { useEffect, useRef, useState, ComponentProps } from "react";

// Components
import Industries from "components/Talent/Industries";

// Hooks
import { useRedirectOnProjectError } from "utils";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Utils
import { isEmpty } from "lodash";
import { urlPaths } from "enums/urlPaths";

// Store
import { getLoaderState, toggleLoader } from "store/slice/UI/UISlice";
import { IFilterCompanies } from "store/slice/currentSearch/currentSearchSlice.types";

// Types
import { ICapStructureSelected } from "components/Talent/CapitalStructure/capitalStructure.types";
import { TAutocompleteFieldSelectedObjectValue } from "components/Autocomplete/AutocompleteField/AutocompleteField.types";
import {
    ILocationCountry,
    ILocationCountryById,
    ILocationDataById,
    ILocationSearch,
    fetchCountryByIdService,
    fetchLocationByIdService,
    saveProjectByPathService,
    talentCompanyFilterOS,
    updateProjectByPathService,
} from "services";
import { RangeType, RefValues } from "components/Talent/TalentSlider";
import { TGetParams, mapForSuggestedCompaniesChipEntity, requestCompaniesData } from "./Companies.functions";
import { CompanyTalent } from "services/company";
import { ChipEntity } from "types";
import { ISelectedTransactionsTypes, DatePicker } from "../TransactionDeals/TransactionDeals.types";
import { TIndustryObj } from "components/Talent/Industries/Industries.types";
import { ProjectCompanySet, ProjectSearchAICreationResponse } from "services/projects/project.types";
import { TransactionsList } from "../TransactionDeals/TransactionMockData";
import capStructureData from "../CapitalStructure/capStructureData";
import { sliderDefaultValEbitdaTurnover, sliderDefaultValEmployees } from "./useEbitdaTurnover";
import {
    getInitialFetchResponse,
    getListsFetchResponse,
    getInsightFilters,
    getSearchAiCompaniesFilters,
    getSearchAiCompaniesLonglist,
    getSearchAiPeers,
    TInitialFetch,
    TInitialFetchError,
} from "pages/ProjectDashboard/adapters/formattedData";
import { SectorChipEntity } from "../SectorsProducsLayout/SectorsProductsLayout";
import { shallowEqual } from "react-redux";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import {
    getSearchAiById,
    fetchProjectSearchAi,
    updateSearchAiCompanyChild,
} from "store/slice/Project/searchAis/searchAisSlice";
import {
    fetchCompanySet,
    getCompanySetByPath,
    updateCompanySet,
} from "store/slice/Project/companiesSet/companiesSetSlice";

type UseCompaniesReturn = {
    refEbitda: React.MutableRefObject<RefValues>;
    refTurnover: React.MutableRefObject<RefValues>;
    refEmployees: React.MutableRefObject<RefValues>;
    enabledEBITDA: boolean;
    enabledTurnover: boolean;
    enabledEmployees: boolean;
    suggestedPeersPath: string;
    onSaveCompanies: (selectedCompanies: CompanyTalent[], filters: IFilterCompanies) => Promise<void>;
    setEnabledEBITDA: React.Dispatch<React.SetStateAction<boolean>>;
    setEnabledTurnover: React.Dispatch<React.SetStateAction<boolean>>;
    setEnabledEmployees: React.Dispatch<React.SetStateAction<boolean>>;
    filterSliderValues: (blockCheckbox: boolean, refSlider: RefValues, sliderValues: number[]) => number[];
    adjustSliderBoxes: (filters: IFilterCompanies, params: TGetParams) => void;
};

const useCompanies = (
    setSelectedCompanies: React.Dispatch<React.SetStateAction<TAutocompleteFieldSelectedObjectValue[]>>,
    setSelectedCompaniesTable: React.Dispatch<React.SetStateAction<CompanyTalent[]>>,
    setSelectedSectors: React.Dispatch<React.SetStateAction<SectorChipEntity[]>>,
    setSelectedProducts: React.Dispatch<React.SetStateAction<ChipEntity[]>>,
    setSelectedIndustries: React.Dispatch<React.SetStateAction<TIndustryObj>>,
    setSelectedTransactionsTypes: React.Dispatch<React.SetStateAction<ISelectedTransactionsTypes[]>>,
    setStartDate: React.Dispatch<React.SetStateAction<DatePicker>>,
    setEndDate: React.Dispatch<React.SetStateAction<DatePicker>>,
    setMostRecentDeal: React.Dispatch<React.SetStateAction<boolean>>,
    setEnableDateRange: React.Dispatch<React.SetStateAction<boolean>>,
    setCity: React.Dispatch<React.SetStateAction<ILocationSearch>>,
    setRadius: React.Dispatch<React.SetStateAction<string | number>>,
    setCountry: React.Dispatch<React.SetStateAction<ILocationCountry>>,
    setCapStructure: React.Dispatch<React.SetStateAction<ICapStructureSelected[]>>,
    onChangeEbitda: (range: RangeType) => void,
    onChangeTurnover: (range: RangeType) => void,
    onChangeEmployees: (range: RangeType) => void,
    selectedCompaniesTable: CompanyTalent[],
    minLinear: number,
    maxLinear: number,
    invertedLogScale: (x: number) => number,
    invertedLogScaleEmployees: (x: number) => number,
    setShouldFetchSectorsAndProducts: React.Dispatch<React.SetStateAction<boolean>>
): UseCompaniesReturn => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    // Url params
    const { companyId: companyIdParams } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");
    const briefId = searchParams.get("briefId");
    const searchAiId = searchParams.get("searchAiId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([projectId, insightId, searchAiId]);

    const refEbitda = useRef<RefValues>({
        update: false,
        checkboxes: { minimum: true, maximum: true },
        inputRange: [null, null],
        range: { minimum: minLinear, maximum: maxLinear },
    });
    const refTurnover = useRef<RefValues>({
        update: false,
        checkboxes: { minimum: true, maximum: true },
        inputRange: [null, null],
        range: { minimum: minLinear, maximum: maxLinear },
    });
    const refEmployees = useRef<RefValues>({
        update: false,
        checkboxes: { minimum: true, maximum: true },
        inputRange: [null, null],
        range: { minimum: minLinear, maximum: maxLinear },
    });

    const [enabledEBITDA, setEnabledEBITDA] = useState(false);
    const [enabledTurnover, setEnabledTurnover] = useState(false);
    const [enabledEmployees, setEnabledEmployees] = useState(false);

    const [suggestedPeersPath, setSuggestedPeersPath] = useState("");
    const [companyListPath, setCompanyListPath] = useState("");

    // Data
    const isLoaderVisible = useAppSelector(getLoaderState);
    const { data: searchAiData } = useAppSelector(getSearchAiById(Number(searchAiId)), shallowEqual);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const longListCompanySetData = useAppSelector(getCompanySetByPath(companyListPath), shallowEqual);
    const companyId = companyIdParams || getInsightFilters(insightData)?.data.company.id || undefined;

    useEffect(() => {
        const initialFetch = async () => {
            const promises = [];
            let result = {} as TInitialFetch | TInitialFetchError;

            dispatch(toggleLoader(true));

            if (isEmpty(searchAiData) && projectId && searchAiId) {
                promises.push(
                    dispatch(
                        fetchProjectSearchAi({
                            projectId: Number(projectId),
                            path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`,
                        })
                    )
                );
            }

            if (isEmpty(insightData) && projectId && insightId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (promises.length && projectId) {
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    dispatch(toggleLoader(false));
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const searchAiParam = "searchAi" in result ? result.searchAi : searchAiData;

            await setData(searchAiParam);

            dispatch(toggleLoader(false));
        };

        const setData = async (searchAiData: ProjectSearchAICreationResponse | undefined) => {
            if (searchAiData === undefined || projectId === null) return;

            // Company Lists (suggested peers and long list)
            const promises = [];
            const suggestedPeersPath = getSearchAiPeers(searchAiData)?.ref?.path;
            const companyLongListPath = getSearchAiCompaniesLonglist(searchAiData)?.ref?.path;

            if (suggestedPeersPath) {
                promises.push(dispatch(fetchCompanySet({ projectId, path: suggestedPeersPath })));
            }

            if (companyLongListPath) {
                promises.push(dispatch(fetchCompanySet({ projectId, path: companyLongListPath })));
            }

            const response = await Promise.allSettled(promises);
            const result = getListsFetchResponse<ProjectCompanySet>(response);

            if ("error" in result) {
                isLoaderVisible && dispatch(toggleLoader(false));
                console.error(result.reason);
                return;
            }

            const [suggestedPeers, companyLongList] = result;

            // Filters
            const currentSearchAICompanies = getSearchAiCompaniesFilters(searchAiData);

            const peersListMeta = suggestedPeers?.meta_ui?.company_ids;
            const peersList = suggestedPeers?.children?.map((peer) => {
                const metaUI = peersListMeta?.find((peerMeta) => peerMeta.id === peer.data.id);
                return { ...peer?.data, selected: metaUI?.selected ?? true };
            });

            const filtersCompanies = currentSearchAICompanies?.data;
            const isNewSearch = !companyLongList?.data.company_ids.length;

            if (filtersCompanies && !isEmpty(filtersCompanies)) {
                // Peers
                suggestedPeersPath && setSuggestedPeersPath(suggestedPeersPath);
                peersList && setSelectedCompanies(peersList.map(mapForSuggestedCompaniesChipEntity));

                // Sectors (Industries)
                if (filtersCompanies.sub_industries) {
                    const selectedSectors = filtersCompanies.sub_industries.gics_sector_ids.map((sectorId) => {
                        const group = filtersCompanies.sub_industries.gics_industry_group_ids.filter((groupId) =>
                            groupId.toString().includes(sectorId.toString())
                        )[0];

                        // Since this is merged with industries value could be and empty string
                        return {
                            key: sectorId,
                            value: "",
                            selected: true,
                            gics_industry_group_id: group,
                        };
                    });
                    setSelectedSectors(selectedSectors);
                    setSelectedIndustries(
                        {
                            sectors: filtersCompanies.sub_industries.gics_sector_ids,
                            industryGroups: filtersCompanies.sub_industries.gics_industry_group_ids,
                            industries: filtersCompanies.sub_industries.gics_industry_ids,
                            subIndustries: filtersCompanies.sub_industries.gics_sub_industry_ids,
                        } || ({} as ComponentProps<typeof Industries>["industries"])
                    );
                }

                // Products
                if (filtersCompanies.products) {
                    const filtersCompaniesMeta = currentSearchAICompanies?.meta_ui;
                    const selectedProducts = filtersCompanies.products.map((p) => {
                        const metaUI = filtersCompaniesMeta?.products.find((productMeta) => p.id === productMeta.id);
                        return {
                            key: p.id,
                            value: p.name,
                            selected: metaUI?.selected ?? true,
                        };
                    });
                    setSelectedProducts(selectedProducts);
                }

                // Transaction Types
                if (filtersCompanies.transactions && !isEmpty(filtersCompanies.transactions)) {
                    // Types
                    if (filtersCompanies.transactions.transaction_type_ids.length) {
                        setSelectedTransactionsTypes(
                            TransactionsList.reduce((acc, transactionType) => {
                                return filtersCompanies.transactions?.transaction_type_ids.includes(transactionType.id)
                                    ? [
                                          ...acc,
                                          {
                                              key: transactionType.id,
                                              value: transactionType.name,
                                              title: transactionType.label,
                                              selected: true,
                                          },
                                      ]
                                    : acc;
                            }, [] as ISelectedTransactionsTypes[])
                        );
                    }

                    // Date range
                    if (!isEmpty(filtersCompanies.transactions.date_range)) {
                        const { date_from, date_to, date_to_today } = filtersCompanies.transactions.date_range;

                        setEnableDateRange(Boolean(date_from || date_to));
                        setStartDate({ value: date_from !== null ? new Date(date_from) : null });
                        setEndDate({
                            value: date_to !== null ? new Date(date_to) : date_to_today ? new Date() : null,
                            isPresent: date_to_today,
                        });
                    }

                    // Most Recent Transactions
                    if (filtersCompanies.transactions.hasOwnProperty("most_recent")) {
                        setMostRecentDeal(filtersCompanies.transactions.most_recent);
                    }
                }

                // Capital Structure
                if (filtersCompanies.transactions?.capital_structure.length) {
                    const selectedCapitalStructures = capStructureData.reduce((acc, capitalStructure) => {
                        return filtersCompanies.transactions?.capital_structure.includes(
                            capitalStructure.tagName.toUpperCase()
                        )
                            ? [...acc, { ...capitalStructure, selected: true }]
                            : [...acc, capitalStructure];
                    }, [] as ICapStructureSelected[]);

                    if (selectedCapitalStructures.length) {
                        setCapStructure(selectedCapitalStructures);
                    }
                }

                // Location
                if (filtersCompanies.geo_radius && !isEmpty(filtersCompanies.geo_radius)) {
                    fetchLocationData(filtersCompanies.geo_radius);
                }

                // Ebidta
                if (filtersCompanies.ebitda_range) {
                    const { min, max } = filtersCompanies.ebitda_range;

                    refEbitda.current = {
                        update: min === null && max === null ? false : true,
                        checkboxes: {
                            minimum: min !== null,
                            maximum: max !== null,
                        },
                        inputRange: [
                            min || sliderDefaultValEbitdaTurnover[0],
                            max || sliderDefaultValEbitdaTurnover[1],
                        ],
                        range: {
                            minimum: min ? invertedLogScale(min) : minLinear,
                            maximum: max ? invertedLogScale(max) : maxLinear,
                        },
                    };
                    onChangeEbitda({
                        minimum: min || sliderDefaultValEbitdaTurnover[0],
                        maximum: max || sliderDefaultValEbitdaTurnover[1],
                    });
                    setEnabledEBITDA(Boolean(min || max));
                }

                // Turnover
                if (filtersCompanies.turnover_range) {
                    const { min, max } = filtersCompanies.turnover_range;

                    refTurnover.current = {
                        update: min === null && max === null ? false : true,
                        checkboxes: {
                            minimum: min !== null,
                            maximum: max !== null,
                        },
                        inputRange: [
                            min || sliderDefaultValEbitdaTurnover[0],
                            max || sliderDefaultValEbitdaTurnover[1],
                        ],
                        range: {
                            minimum: min ? invertedLogScale(min) : minLinear,
                            maximum: max ? invertedLogScale(max) : maxLinear,
                        },
                    };
                    onChangeTurnover({
                        minimum: min || sliderDefaultValEbitdaTurnover[0],
                        maximum: max || sliderDefaultValEbitdaTurnover[1],
                    });
                    setEnabledTurnover(Boolean(min || max));
                }

                // Employees
                if (filtersCompanies.employees_range) {
                    const { min, max } = filtersCompanies.employees_range;

                    refEmployees.current = {
                        update: min === null && max === null ? false : true,
                        checkboxes: {
                            minimum: min !== null,
                            maximum: max !== null,
                        },
                        inputRange: [min || sliderDefaultValEmployees[0], max || sliderDefaultValEmployees[1]],
                        range: {
                            minimum: min ? invertedLogScaleEmployees(min) : minLinear,
                            maximum: max ? invertedLogScaleEmployees(max) : maxLinear,
                        },
                    };
                    onChangeEmployees({
                        minimum: min || sliderDefaultValEmployees[0],
                        maximum: max || sliderDefaultValEmployees[1],
                    });
                    setEnabledEmployees(Boolean(min || max));
                }
            }

            if (isNewSearch) {
                setShouldFetchSectorsAndProducts(true);
            }

            companyLongListPath && setCompanyListPath(companyLongListPath);

            if (companyLongList?.data?.company_ids?.length) {
                fetchLonglistData(companyLongList.data.company_ids);
            }
        };

        const fetchLocationData = async (locationData: any) => {
            try {
                if (locationData.country_id) {
                    const { data } = await fetchCountryByIdService<ILocationCountryById>(locationData.country_id);

                    data &&
                        setCountry({
                            id: data.country_id,
                            name: data.country_record.name,
                            code_iso_alpha2: data.country_record.code_iso_alpha2,
                            code_iso_alpha3: data.country_record.code_iso_alpha3,
                        });
                }

                if (locationData.city_id) {
                    const { data } = await fetchLocationByIdService<ILocationDataById>(locationData.city_id);

                    data &&
                        setCity({
                            id: data.location_id,
                            city: data.location_record.city,
                            country_id: data.location_record.country_id,
                            country: data.location_record.country,
                            location: data.location_record.location,
                        });
                }
            } catch (error) {
                console.error(error);
            }

            if (locationData.geo_radius?.radius) {
                setRadius(locationData.geo_radius.radius);
            }
        };

        const fetchLonglistData = async (companiesLonglistIds: number[]) => {
            const params = talentCompanyFilterOS.filter((cols) => !cols.includes("sector") || !cols.includes("domain"));
            const companiesWithData = await requestCompaniesData<CompanyTalent>(companiesLonglistIds, params);
            setSelectedCompaniesTable(companiesWithData);
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSaveCompanies = async (selectedCompanies: CompanyTalent[], filters: IFilterCompanies) => {
        if (selectedCompanies.length && projectId && searchAiId && insightId) {
            dispatch(toggleLoader(true));

            if (companyListPath) {
                // Updating
                try {
                    const companyIds = selectedCompanies.map((company) => company.id);

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: companyListPath,
                        data: { company_ids: companyIds },
                    });

                    if (longListCompanySetData) {
                        const updatedData = {
                            ...longListCompanySetData,
                            children: selectedCompanies.map((company) => ({
                                meta: {
                                    path: `/project_930/company_set_list/company_set_${longListCompanySetData.data.id}/company_${company.id}`,
                                    type: "PROJECT_COMPANY" as const,
                                    children_count: 0,
                                },
                                data: {
                                    id: company.id,
                                    name: company.name,
                                    transactions: company.transactions,
                                    current_capital_structure:
                                        company.transactions.find((t) => t.is_most_recent)?.capital_structure ?? "",
                                },
                            })),
                            meta: { ...longListCompanySetData.meta, count: companyIds.length },
                            data: { ...longListCompanySetData.data, company_ids: companyIds },
                        };
                        dispatch(updateCompanySet(updatedData));
                    }
                } catch (error) {
                    console.error(error);
                    dispatch(toggleLoader(false));
                    return;
                }
            } else {
                // Creating
                try {
                    const { data } = await saveProjectByPathService({
                        id: Number(projectId),
                        project_path: `/project_${projectId}/company_set_list`,
                        data: {
                            company_ids: selectedCompaniesTable.map((company) => company.id),
                            name: `Selected companies ` + `${new Date().getTime()}`.slice(-5),
                            type: "PEERS",
                        },
                        echo: true,
                    });

                    const companySet = data.children.at(-1);
                    const companySetPath = companySet.meta.path;

                    const payload = {
                        data: {},
                        ref: { path: companySetPath },
                    };

                    await updateProjectByPathService({
                        id: Number(projectId),
                        project_path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}/search_companies/selected`,
                        data: payload,
                    });

                    const insightCompanySetRef = getSearchAiCompaniesLonglist(searchAiData);

                    if (insightCompanySetRef && searchAiId) {
                        const updatedData = {
                            ...insightCompanySetRef,
                            ref: payload.ref,
                        };
                        dispatch(
                            updateSearchAiCompanyChild({
                                data: updatedData,
                                id: Number(searchAiId),
                                type: "PROJECT_COMPANY_SET_REF_B",
                            })
                        );
                    }
                } catch (error) {
                    console.error(error);
                    dispatch(toggleLoader(false));
                    return;
                }
            }

            navigate(
                `${urlPaths.TalentPeople}/${companyId}?projectId=${projectId}&insightId=${insightId}&briefId=${briefId}&searchAiId=${searchAiId}`
            );
        }
    };

    const filterSliderValues = (blockCheckboxState: boolean, sliderRefValues: RefValues, values: number[]) => {
        if (blockCheckboxState || (sliderRefValues.checkboxes.maximum && sliderRefValues.checkboxes.minimum)) {
            return values;
        } else {
            return [];
        }
    };

    const adjustSliderBoxes = (filters: IFilterCompanies, params: TGetParams): void => {
        const minEBITDA = filters.selectedEbitda.sliderValues.checkboxes.minimum;
        const maxEBITDA = filters.selectedEbitda.sliderValues.checkboxes.maximum;

        if (filters.checkboxEnabledEBITDA && (minEBITDA || maxEBITDA) !== false) {
            params.filters.ebitda = {
                ...(minEBITDA && { minimum: filters.selectedEbitda.ebitda[0] }),
                ...(maxEBITDA && { maximum: filters.selectedEbitda.ebitda[1] }),
            };
        }

        const minTurnover = filters.selectedTurnover.sliderValues.checkboxes.minimum;
        const maxTurnover = filters.selectedTurnover.sliderValues.checkboxes.maximum;

        if (filters.checkboxEnabledTurnover && (minTurnover || maxTurnover) !== false) {
            params.filters.turnover = {
                ...(minTurnover && { minimum: filters.selectedTurnover.turnover[0] }),
                ...(maxTurnover && { maximum: filters.selectedTurnover.turnover[1] }),
            };
        }

        const minEmployees = filters.selectedEmployees.sliderValues.checkboxes.minimum;
        const maxEmployees = filters.selectedEmployees.sliderValues.checkboxes.maximum;

        if (filters.checkboxEnabledEmployees && (minEmployees || maxEmployees) !== false) {
            params.filters.employees = {
                ...(minEmployees && { minimum: filters.selectedEmployees.employees[0] }),
                ...(maxEmployees && { maximum: filters.selectedEmployees.employees[1] }),
            };
        }
    };

    return {
        refEbitda,
        refTurnover,
        refEmployees,
        enabledEBITDA,
        enabledTurnover,
        enabledEmployees,
        suggestedPeersPath,
        onSaveCompanies,
        setEnabledEBITDA,
        setEnabledTurnover,
        setEnabledEmployees,
        filterSliderValues,
        adjustSliderBoxes,
    };
};

export default useCompanies;
